import Icon, { type IconName } from 'components/Icon';
import type { HTMLAttributes } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

export type Size = 'md' | 'sm';
export type Variant = 'error' | 'primary' | 'secondary';

interface BaseProps extends HTMLAttributes<HTMLSpanElement> {
	className?: string;
	size?: Size;
	variant?: Variant;
}

interface PropsWithText extends BaseProps {
	icon?: never;
	text: string | number | undefined;
}
interface PropsWithIcon extends BaseProps {
	icon: IconName;
	text?: never;
}

type Props = PropsWithText | PropsWithIcon;

/** A number as a rounded badge. */
export default function Badge({
	className,
	icon,
	size = 'md',
	text,
	variant = 'primary',
	...attrs
}: Props) {
	if (is.nonZeroFalsy(text) && !icon) {
		return null;
	}

	return (
		<span
			{...attrs}
			className={cn(
				className,
				'inline-flex shrink-0 items-center justify-center rounded-full',
				'font-body font-bold',
				// Design system uses border but outline is used here to avoid
				// affecting layout between normal and forced colors mode.
				'outline-2 outline-inverse outline-solid forced-colors:outline-1',
				variant === 'primary' && 'bg-badge text-inverse',
				variant === 'secondary' && 'bg-base text-base',
				variant === 'error' && 'bg-badgeError text-inverse',
				size === 'sm' && ['h-4 min-w-4 text-xs', text && 'px-1'],
				size === 'md' && ['h-5 min-w-5 text-sm', text && 'px-1.5'],
			)}
		>
			{icon && <Icon name={icon} size={size === 'sm' ? 12 : 16} />}
			{text && text}
		</span>
	);
}
Badge.displayName = 'Badge';
